import { Button } from '@/components/ui/button.js';
import { useAuth } from '@/lib/auth/use-auth.js';
import { isEmbedded } from '@/lib/embed/embed-protocol.js';
import { tokens } from '@/lib/tokens.js';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState, useTransition } from 'react';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

// W-5: block access in production when not embedded
function ProductionUnavailable() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colors.bgDark,
        padding: '1.5rem',
        fontFamily: tokens.font.sans,
      }}
    >
      <div
        style={{
          color: tokens.colors.textMutedDark,
          fontSize: '0.875rem',
          textAlign: 'center',
        }}
      >
        <p>This page is not available in production.</p>
      </div>
    </div>
  );
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  // W-5: use input instead of textarea; show/hide toggle
  const inputRef = useRef<HTMLInputElement>(null);
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<{ code?: string; message: string } | null>(null);
  const [isPending, startTransition] = useTransition();

  // W-5: guard — en producción, /login solo es accesible:
  //   (a) cuando la app está embebida en el ERP (flujo normal de auth via iframe), o
  //   (b) cuando VITE_ALLOW_DEV_LOGIN === 'true' en tiempo de build
  //       (escape hatch para probar producción standalone sin integración ERP).
  const devLoginAllowed = import.meta.env.VITE_ALLOW_DEV_LOGIN === 'true';
  if (import.meta.env.PROD && !isEmbedded() && !devLoginAllowed) {
    return <ProductionUnavailable />;
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const token = inputRef.current?.value.trim() ?? '';
    if (token.length === 0) return;

    setError(null);

    startTransition(() => {
      auth
        .exchange(token)
        .then(() => {
          void navigate({ to: '/catalog' });
        })
        .catch((err: unknown) => {
          if (
            err !== null &&
            typeof err === 'object' &&
            'code' in err &&
            typeof (err as Record<string, unknown>).code === 'string'
          ) {
            setError({
              code: String((err as Record<string, unknown>).code),
              message: err instanceof Error ? err.message : 'Authentication failed',
            });
          } else {
            setError({
              message: err instanceof Error ? err.message : 'Authentication failed',
            });
          }
        });
    });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: tokens.colors.bgDark,
        padding: '1.5rem',
        fontFamily: tokens.font.sans,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          backgroundColor: tokens.colors.bgCardDark,
          borderRadius: tokens.radius.card,
          border: `1px solid ${tokens.colors.borderDark}`,
          padding: '2rem',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -1px rgba(0,0,0,.06)',
        }}
      >
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <img
            src={tokens.logo.dark}
            alt="Quartup"
            style={{ height: '32px', marginBottom: '1.5rem' }}
          />
          <h1
            style={{
              fontSize: '1.25rem',
              fontWeight: 700,
              color: tokens.colors.textDark,
              margin: 0,
            }}
          >
            Dev Login
          </h1>
          <p
            style={{
              fontSize: '0.875rem',
              color: tokens.colors.textMutedDark,
              marginTop: '0.5rem',
            }}
          >
            Paste your ERP token to authenticate. Available in development only.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="erp-token"
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: tokens.colors.textDark,
                marginBottom: '0.5rem',
              }}
            >
              ERP Token
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="erp-token"
                ref={inputRef}
                type={showToken ? 'text' : 'password'}
                placeholder="Paste ERP token here"
                required
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '0.5rem 4rem 0.5rem 0.75rem',
                  backgroundColor: tokens.colors.bgDark,
                  color: tokens.colors.textDark,
                  border: `1px solid ${error !== null ? tokens.colors.error : tokens.colors.borderDark}`,
                  borderRadius: tokens.radius.input,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: tokens.colors.textMutedDark,
                  padding: '0.25rem',
                }}
              >
                {showToken ? 'Hide' : 'Show'}
              </button>
            </div>
            <p
              style={{
                fontSize: '0.75rem',
                color: tokens.colors.textMutedDark,
                marginTop: '0.25rem',
              }}
            >
              Never share your ERP token — it grants full ERP access.
            </p>
          </div>

          {error !== null && (
            <div
              role="alert"
              style={{
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                border: `1px solid ${tokens.colors.error}`,
                borderRadius: tokens.radius.card,
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              {error.code !== undefined && (
                <span
                  style={{
                    display: 'inline-block',
                    fontWeight: 600,
                    color: tokens.colors.error,
                    marginRight: '0.5rem',
                  }}
                >
                  [{error.code}]
                </span>
              )}
              <span style={{ color: tokens.colors.textDark }}>{error.message}</span>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isPending}
            style={{ width: '100%' }}
          >
            {isPending ? 'Authenticating…' : 'Sign in'}
          </Button>
        </form>

        <div
          style={{
            marginTop: '1.5rem',
            padding: '0.75rem',
            backgroundColor: tokens.colors.bgDark,
            borderRadius: tokens.radius.card,
            fontSize: '0.75rem',
            color: tokens.colors.textMutedDark,
            fontFamily: 'monospace',
          }}
        >
          <p style={{ margin: '0 0 0.25rem 0', fontWeight: 600, fontFamily: tokens.font.sans }}>
            How to get a token:
          </p>
          <code>
            curl https://erp.quartup.net/api/test/auth \<br />
            &nbsp;&nbsp;-H &apos;Authorization: &lt;API_KEY&gt;&apos;
          </code>
        </div>
      </div>
    </div>
  );
}
